<script lang="ts">
	import { Button, Text } from '@Shelly/shelly-design-system';
	import { format } from 'svelte-i18n';
	import { getDeviceSettings } from 'src/stores/device-runtime/status-selectors';
	import { real_id } from 'src/helpers/interface_helpers';
	import type { VentoraFanConfig } from 'src/types/ventora_fan';
	import VentoraFanNumericInput from './VentoraFanNumericInput.svelte';
	import {
		VENTORA_BOOST_DEFAULTS_PRIORITY_HINT,
		VENTORA_BOOST_DURATION_HINT,
		VENTORA_BOOST_SPEED_HINT,
	} from './ventora_fan_field_copy';
	import {
		defaultVentoraFanConfig,
		patchVentoraFanConfig,
		resolveVentoraFanConfig,
		VENTORA_FAN_COMPONENT_KEY,
		ventoraBoostLimits,
		ventoraClamp,
		ventoraFiniteNumberFromInput,
		ventoraHasValidationErrors,
		ventoraNumericValidation,
		ventoraToNumber,
	} from './ventora_fan_settings_helpers';

	export let device;
	export let isOffline: boolean | undefined;
	export let test_setting: boolean | undefined;
	export let useNewLayout: boolean | undefined;

	const id = real_id(device.id);

	let loading = false;
	let dirty = false;
	let draftSpeed = defaultVentoraFanConfig.boost.speed;
	let draftDuration = defaultVentoraFanConfig.boost.duration;

	$: settingsStore = getDeviceSettings(device.id);
	$: config = resolveVentoraFanConfig(
		$settingsStore?.[VENTORA_FAN_COMPONENT_KEY] as Partial<VentoraFanConfig> | undefined,
	);

	$: if (!dirty) {
		draftSpeed = config.boost.speed;
		draftDuration = config.boost.duration;
	}

	$: speedValidation = ventoraNumericValidation(draftSpeed, ...ventoraBoostLimits.speed);
	$: durationValidation = ventoraNumericValidation(draftDuration, ...ventoraBoostLimits.duration);
	$: hasValidationErrors = ventoraHasValidationErrors([speedValidation, durationValidation]);

	const inputSpeed = (e: Event) => {
		dirty = true;
		const n = ventoraFiniteNumberFromInput((e.currentTarget as HTMLInputElement).value);
		if (n !== undefined) draftSpeed = n;
	};

	const blurSpeed = (e: Event) => {
		const [min, max] = ventoraBoostLimits.speed;
		draftSpeed = ventoraClamp(
			ventoraToNumber((e.currentTarget as HTMLInputElement).value, draftSpeed),
			min,
			max,
		);
	};

	const inputDuration = (e: Event) => {
		dirty = true;
		const n = ventoraFiniteNumberFromInput((e.currentTarget as HTMLInputElement).value);
		if (n !== undefined) draftDuration = n;
	};

	const blurDuration = (e: Event) => {
		const [min, max] = ventoraBoostLimits.duration;
		draftDuration = ventoraClamp(
			ventoraToNumber((e.currentTarget as HTMLInputElement).value, draftDuration),
			min,
			max,
		);
	};

	const clampDraftBoost = () => {
		const [sMin, sMax] = ventoraBoostLimits.speed;
		const [dMin, dMax] = ventoraBoostLimits.duration;
		draftSpeed = ventoraClamp(draftSpeed, sMin, sMax);
		draftDuration = ventoraClamp(draftDuration, dMin, dMax);
	};

	const save = async () => {
		if (!dirty || hasValidationErrors) return;
		clampDraftBoost();
		loading = true;
		const ok = await patchVentoraFanConfig(device, {
			boost: {
				...config.boost,
				speed: draftSpeed,
				duration: draftDuration,
			},
		});
		if (ok) dirty = false;
		loading = false;
	};
</script>

<Text styleType="body-small" class="mb-m text-color-muted">
	{$format(VENTORA_BOOST_DEFAULTS_PRIORITY_HINT.key, {
		default: VENTORA_BOOST_DEFAULTS_PRIORITY_HINT.default,
	})}
</Text>

<VentoraFanNumericInput
	_id="input-ventora-boost-speed"
	labelKey="ventora_boost_speed"
	labelDefault="Speed"
	hintKey={VENTORA_BOOST_SPEED_HINT.key}
	hintDefault={VENTORA_BOOST_SPEED_HINT.default}
	value={draftSpeed}
	min={ventoraBoostLimits.speed[0]}
	max={ventoraBoostLimits.speed[1]}
	icon="fas fa-gauge-high"
	validation={speedValidation}
	onInput={inputSpeed}
	onBlur={blurSpeed}
/>

<VentoraFanNumericInput
	_id="input-ventora-boost-duration"
	labelKey="ventora_boost_duration"
	labelDefault="Duration (seconds)"
	hintKey={VENTORA_BOOST_DURATION_HINT.key}
	hintDefault={VENTORA_BOOST_DURATION_HINT.default}
	value={draftDuration}
	min={ventoraBoostLimits.duration[0]}
	max={ventoraBoostLimits.duration[1]}
	icon="fas fa-clock"
	validation={durationValidation}
	onInput={inputDuration}
	onBlur={blurDuration}
/>

<Button
	size="s"
	width="full"
	variant="primary"
	disabled={!dirty || hasValidationErrors}
	{loading}
	on:click={save}
	id="button-ventora-boost-save"
>
	{$format('button_save', { default: 'Save' })}
</Button>
